import { Trash2, Layers, Calendar } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface Presentation {
  id: string;
  title: string;
  slide_count: number;
  status: 'generating' | 'completed' | 'failed';
  created_at: string;
}

interface PresentationCardProps {
  presentation: Presentation;
  onClick: () => void;
  onDelete?: (id: string) => void;
}

const STATUS_STYLES: Record<Presentation['status'], string> = {
  generating: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function PresentationCard({
  presentation,
  onClick,
  onDelete,
}: PresentationCardProps) {
  return (
    <Card
      className="group cursor-pointer transition-shadow hover:shadow-md"
      onClick={onClick}
    >
      <CardContent className="relative p-5">
        {/* Delete button (top-right, on hover) */}
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(presentation.id);
            }}
            className="absolute right-3 top-3 hidden rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive group-hover:block"
            title="Delete presentation"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}

        {/* Title */}
        <h3 className="truncate pr-8 text-base font-semibold">
          {presentation.title}
        </h3>

        {/* Meta row */}
        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <Layers className="h-3.5 w-3.5" />
            {presentation.slide_count} slides
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(presentation.created_at).toLocaleDateString()}
          </span>
        </div>

        {/* Status badge */}
        <div className="mt-3">
          <Badge
            variant="outline"
            className={cn('text-[11px] font-medium', STATUS_STYLES[presentation.status])}
          >
            {presentation.status.charAt(0).toUpperCase() + presentation.status.slice(1)}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}
